import React, { useState, useCallback, useEffect } from 'react';
import * as styles from './SaveFileModal.css.ts';
import { getRendererLogger } from '../utils/logger';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';

const logger = getRendererLogger();

export type GraphExportFormat = 'svg'; // 将来的に 'png' | 'pdf' などを追加可能

export interface GraphExportOptions {
  width: number;
  height: number;
}

interface ExportGraphModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filePath: string, format: GraphExportFormat, options: GraphExportOptions) => void | Promise<void>;
  defaultFileName?: string;
  graphTitle?: string;
  /** Initial width from preview (used to calculate aspect ratio) */
  initialWidth?: number;
  /** Initial height from preview (used to calculate aspect ratio) */
  initialHeight?: number;
}

/**
 * グラフエクスポート用モーダル
 * ユーザーがファイルパスと形式を選択できる
 */
export function ExportGraphModal({
  isOpen,
  onClose,
  onExport,
  defaultFileName = 'graph',
  graphTitle = '',
  initialWidth,
  initialHeight
}: ExportGraphModalProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [directory, setDirectory] = useState('');
  const [format, setFormat] = useState<GraphExportFormat>('svg');
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // サイズ設定
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(800 / 600);

  // モーダルが開いたときに初期サイズを設定
  useEffect(() => {
    if (isOpen) {
      const w = initialWidth && initialWidth > 0 ? Math.round(initialWidth) : 800;
      const h = initialHeight && initialHeight > 0 ? Math.round(initialHeight) : 600;
      setWidth(w);
      setHeight(h);
      setAspectRatio(w / h);
    }
  }, [isOpen, initialWidth, initialHeight]);

  // モーダルが開いたときにファイル名を更新
  useEffect(() => {
    if (isOpen && graphTitle) {
      // グラフタイトルから安全なファイル名を生成
      const safeName = graphTitle
        .replace(/[<>:"|?*/\\]/g, '_')  // 危険な文字を置換
        .replace(/\s+/g, '_')             // 空白を置換
        .replace(/-/g, '_')               // ハイフンもアンダースコアに置換
        .replace(/_+/g, '_')              // 連続するアンダースコアを1つに
        .toLowerCase();
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      setFileName(safeName ? `${safeName}_${timestamp}` : `graph_${timestamp}`);
    }
  }, [isOpen, graphTitle]);

  // デフォルトディレクトリを設定
  useEffect(() => {
    if (isOpen) {
      // E2Eテスト環境のチェック
      const testDir = (window as Window & { __testDirectory?: string }).__testDirectory;
      if (testDir) {
        setDirectory(testDir);
        logger.info('[ExportGraphModal] Using test directory', { directory: testDir });
        return;
      }

      // Web版の場合はディレクトリ設定不要
      setDirectory('');
      logger.info('[ExportGraphModal] Web version - no directory needed');
    }
  }, [isOpen]);

  // ファイル名のバリデーション
  const validateFileName = useCallback((name: string): boolean => {
    // 空文字チェック
    if (!name.trim()) {
      setError('File name cannot be empty');
      return false;
    }

    // 無効な文字のチェック
    const invalidChars = /[<>:"|?*/\\]/;
    if (invalidChars.test(name)) {
      setError('File name contains invalid characters');
      return false;
    }

    // 長さチェック
    if (name.length > 255) {
      setError('File name is too long');
      return false;
    }

    setError(null);
    return true;
  }, []);

  // ファイル名の変更処理
  const handleFileNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFileName(newName);
    validateFileName(newName);
  }, [validateFileName]);

  // 拡張子を取得
  const getFileExtension = (fmt: GraphExportFormat): string => {
    switch (fmt) {
      case 'svg':
        return '.svg';
      default:
        return '.svg';
    }
  };

  // エクスポート処理
  const handleExport = useCallback(async () => {
    if (!validateFileName(fileName)) {
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      // 拡張子を追加（必要な場合）
      const extension = getFileExtension(format);
      let fullFileName = fileName;
      if (!fileName.endsWith(extension)) {
        fullFileName = fileName + extension;
      }

      // フルパスを構築
      const fullPath = `${directory}/${fullFileName}`;
      
      logger.info('[ExportGraphModal] Exporting graph', { 
        fileName: fullFileName, 
        directory, 
        fullPath,
        format,
        width: width,
        height: height
      });

      // エクスポートオプション
      const options: GraphExportOptions = {
        width: width,
        height: height
      };

      // onExportがPromiseを返す場合は待つ
      const result = onExport(fullPath, format, options);
      if (result && typeof result.then === 'function') {
        await result;
      }
      
      logger.info('[ExportGraphModal] Export completed');
      onClose();
    } catch (err) {
      logger.error('[ExportGraphModal] Export failed', err as Error);
      setError('Failed to export graph: ' + (err as Error).message);
    } finally {
      setIsExporting(false);
    }
  }, [fileName, directory, format, width, height, validateFileName, onExport, onClose]);

  // キャンセル処理
  const handleCancel = useCallback(() => {
    setFileName(defaultFileName);
    setError(null);
    setIsExporting(false);
    onClose();
  }, [defaultFileName, onClose]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Export Graph"
      width="md"
      closeOnOverlayClick={!isExporting}
      showCloseButton={!isExporting}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isExporting}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={!!error || !fileName.trim() || isExporting}
            data-testid="export-button"
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </>
      }
      data-testid="export-graph-modal"
    >
      <div>
          {/* Web版のみ */}
          <div className={styles.formGroup}>
            <div className={styles.webVersionNote}>
              <p>The file will be downloaded to your browser's default download folder.</p>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="fileName">File Name:</label>
            <div className={styles.fileNameInput}>
              <input
                id="fileName"
                type="text"
                value={fileName}
                onChange={handleFileNameChange}
                className={styles.fileNameField}
                placeholder="Enter file name"
                autoFocus
                data-testid="filename-input"
                disabled={isExporting}
              />
              <span className={styles.extension} data-testid="file-extension">
                {getFileExtension(format)}
              </span>
            </div>
            {error && (
              <div className={styles.error} data-testid="error-message">
                {error}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="format">File Format:</label>
            <select
              id="format"
              value={format}
              onChange={(e) => setFormat(e.target.value as GraphExportFormat)}
              className={styles.directoryField}
              data-testid="format-select"
              disabled={isExporting}
            >
              <option value="svg">SVG (Scalable Vector Graphics)</option>
              {/* 将来的に追加可能:
              <option value="png">PNG (Portable Network Graphics)</option>
              <option value="pdf">PDF (Portable Document Format)</option>
              */}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Export Size:</label>
            <div className={styles.sizeInputContainer}>
              <div className={styles.sizeInputGroup}>
                <label htmlFor="width" className={styles.sizeLabel}>Width:</label>
                <input
                  id="width"
                  type="number"
                  value={width}
                  onChange={(e) => {
                    const newWidth = parseInt(e.target.value) || 100;
                    setWidth(newWidth);
                    if (lockAspectRatio) {
                      setHeight(Math.round(newWidth / aspectRatio));
                    }
                  }}
                  className={styles.sizeInput}
                  min="100"
                  max="10000"
                  disabled={isExporting}
                  data-testid="width-input"
                />
                <span className={styles.sizeUnit}>px</span>
              </div>

              <button
                type="button"
                onClick={() => setLockAspectRatio(!lockAspectRatio)}
                className={styles.aspectRatioLockButton}
                title={lockAspectRatio ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                disabled={isExporting}
                data-testid="aspect-ratio-lock"
              >
                {lockAspectRatio ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a4 4 0 0 0-4 4v3H3a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1V5a4 4 0 0 0-4-4zm2.5 7H5.5V5a2.5 2.5 0 0 1 5 0v3z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a4 4 0 0 0-4 4v1h1V5a3 3 0 0 1 6 0v3H3a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1h-1V5a4 4 0 0 0-4-4z"/>
                  </svg>
                )}
              </button>

              <div className={styles.sizeInputGroup}>
                <label htmlFor="height" className={styles.sizeLabel}>Height:</label>
                <input
                  id="height"
                  type="number"
                  value={height}
                  onChange={(e) => {
                    const newHeight = parseInt(e.target.value) || 100;
                    setHeight(newHeight);
                    if (lockAspectRatio) {
                      setWidth(Math.round(newHeight * aspectRatio));
                    }
                  }}
                  className={styles.sizeInput}
                  min="100"
                  max="10000"
                  disabled={isExporting}
                  data-testid="height-input"
                />
                <span className={styles.sizeUnit}>px</span>
              </div>
            </div>
          </div>

      </div>
    </Dialog>
  );
}